import React, { useState, useEffect } from "react";
import { Card, Collapse, Input, Select, Switch, Button, message, Spin, ConfigProvider } from "antd";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import _ from "lodash";
import "./AICoachSetup.css";

const { TextArea } = Input;
const { Panel } = Collapse;

const POSITIONS = ["QB", "RB", "WR", "TE", "OL", "DL", "LB", "S", "C", "K", "ST"];

const emptyPositionIntel = () =>
  Object.fromEntries(
    POSITIONS.map((p) => [
      p,
      {
        responsibilities: "",
        techniqueStandards: "",
        coachingCues: "",
        drills: "",
        commonErrors: "",
        filmPoints: "",
        gradeHardest: "",
        averageVsGreat: "",
      },
    ])
  );

const DEFAULT_FORM = {
  tone: "encouraging",
  feedbackStyle: ["corrective", "reinforcing"],
  lengthPreference: "quick",
  accountabilityLevel: "growth",
  playerOwnership: true,
  programName: "",
  levelOfPlay: "",
  offensivePhilosophy: "",
  defensivePhilosophy: "",
  specialTeamsPhilosophy: "",
  experienceLevel: "",
  seasonGoals: "",
  coreValues: "",
  nonNegotiables: "",
  doingItRight: "",
  languageOften: "",
  whatGetsPraised: "",
  whatGetsCorrected: "",
  faqQuestions: "",
  preferredResponses: "",
  ocIdentity: "",
  ocConcepts: "",
  dcIdentity: "",
  dcConcepts: "",
  stPhilosophy: "",
  keyMetrics: "",
  effortVsExecution: "",
  temperature: 0.3,
  maxResponseTokens: 300,
  useConversationMemory: true,
  maxConversationTurns: 10,
  enabledMcpTools: ["player-stats", "playbook", "feedback-history"],
  offLimitsTopics: "playing time decisions, college recruiting, medical advice, other players' performance",
  escalationWhen: "sensitive topics, playing time, health, or when player asks for a human",
};

const SettingRow = ({ label, hint, children }) => (
  <div className="ai-setup-row">
    <div className="ai-setup-label">{label}</div>
    {children}
    {hint && <div className="ai-setup-hint">{hint}</div>}
  </div>
);

const AICoachSetup = ({ coachProfile, getAiCoachConfig, saveAiCoachConfig }) => {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [positionIntel, setPositionIntel] = useState(emptyPositionIntel());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const schoolId = _.get(coachProfile, "school.id") || _.get(coachProfile, "school_id");

  useEffect(() => {
    if (!schoolId) {
      setLoading(false);
      return;
    }
    getAiCoachConfig({
      params: { schoolId },
      callback: (ok, data) => {
        setLoading(false);
        if (ok && _.get(data, "data")) {
          const d = data.data.data;

          console.log("d", d);
          if (d.form) setForm((prev) => ({ ...DEFAULT_FORM, ...prev, ...d.form }));
          if (d.positionIntel) setPositionIntel((prev) => ({ ...emptyPositionIntel(), ...prev, ...d.positionIntel }));
        }
      },
    });
  }, [schoolId, getAiCoachConfig]);

  useEffect(() => {
    console.log("form", form);
  }, [form]);

  const handleSave = () => {
    if (!schoolId) {
      message.warning("No school selected.");
      return;
    }
    setSaving(true);
    saveAiCoachConfig({
      params: { schoolId },
      body: { form, positionIntel },
      callback: (ok) => {
        setSaving(false);
        if (ok) message.success("AI Coach setup saved.");
        else message.error("Failed to save.");
      },
    });
  };

  const update = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const updatePosition = (pos, field, value) =>
    setPositionIntel((prev) => ({
      ...prev,
      [pos]: { ...prev[pos], [field]: value },
    }));

  const SaveFooter = () => (
    <div className="ai-setup-panel-footer">
      <Button type="primary" className="ai-coach-setup-save" onClick={handleSave} loading={saving}>Save</Button>
    </div>
  );

  if (loading) {
    return (
      <div className="ai-coach-setup ai-coach-setup-loading">
        <Spin size="large" />
        <span className="ai-coach-setup-loading-text">Loading your AI Coach setup…</span>
      </div>
    );
  }

  const theme = { token: { colorPrimary: "#0071e3", borderRadius: 8 } };

  return (
    <ConfigProvider theme={theme}>
      <div className="ai-coach-setup">
        <div className="ai-coach-setup-header">
          <div className="ai-coach-setup-header-top">
            <div>
              <h1 className="ai-coach-setup-title">AI Coach: Team Intelligence Setup</h1>
              <p className="ai-coach-setup-subtitle">
                Build the shared coaching intelligence that powers your team’s AI Coach Chat.
                Reinforces staff language, techniques, and expectations on every play, for every player.
              </p>
            </div>
            <Link to="/ai-coach-chat-analytics" className="ai-coach-setup-analytics-btn">Chat Analytics</Link>
          </div>
        </div>

        <Collapse defaultActiveKey={["action", "team-profile"]} className="ai-setup-collapse">
        <Panel header="1. Action (How the AI Should Respond)" key="action">
          <Card size="small" className="ai-setup-card">
            <div className="ai-setup-fields-row">
              <SettingRow
                label="Tone of feedback"
                hint="Used by AI to match your staff’s coaching voice (e.g. demanding, encouraging, teacher-first, direct)."
              >
                <Select
                  value={form.tone}
                  onChange={(v) => update("tone", v)}
                  options={[
                    { value: "demanding", label: "Demanding" },
                    { value: "encouraging", label: "Encouraging" },
                    { value: "teacher-first", label: "Teacher-first" },
                    { value: "direct", label: "Direct" },
                  ]}
                  className="ai-setup-input"
                />
              </SettingRow>
              <SettingRow
                label="Feedback style"
                hint="Used by AI to deliver feedback that sounds like your program: corrective, reinforcing, or question-based (prompts player thinking)."
              >
                <Select
                  mode="multiple"
                  value={form.feedbackStyle}
                  onChange={(v) => update("feedbackStyle", v)}
                  options={[
                    { value: "corrective", label: "Corrective" },
                    { value: "reinforcing", label: "Reinforcing" },
                    { value: "question-based", label: "Question-based" },
                  ]}
                  className="ai-setup-input"
                />
              </SettingRow>
            </div>
            <div className="ai-setup-fields-row">
              <SettingRow
                label="Length preference"
                hint="Used by AI to keep responses as quick coaching cues or detailed explanations."
              >
                <Select
                  value={form.lengthPreference}
                  onChange={(v) => update("lengthPreference", v)}
                  options={[
                    { value: "quick", label: "Quick coaching cue" },
                    { value: "detailed", label: "Detailed explanation" },
                  ]}
                  className="ai-setup-input"
                />
              </SettingRow>
              <SettingRow
                label="Accountability level"
                hint="Used by AI to set high standard / no fluff vs growth-focused tone."
              >
                <Select
                  value={form.accountabilityLevel}
                  onChange={(v) => update("accountabilityLevel", v)}
                  options={[
                    { value: "high", label: "High standard / no fluff" },
                    { value: "growth", label: "Growth-focused" },
                  ]}
                  className="ai-setup-input"
                />
              </SettingRow>
            </div>
            <SettingRow
              label="Player ownership"
              hint="Used by AI to ask players to self-reflect and explain the “why,” building football IQ."
            >
              <Switch
                checked={form.playerOwnership}
                onChange={(v) => update("playerOwnership", v)}
              />
            </SettingRow>
          </Card>
          <SaveFooter />
        </Panel>

        <Panel header="2. Team Profile" key="team-profile">
          <Card size="small" className="ai-setup-card">
            <div className="ai-setup-fields-row">
              <SettingRow
                label="School / program name"
                hint="Used by AI to frame answers in your program’s context."
              >
                <Input
                  value={form.programName}
                  onChange={(e) => update("programName", e.target.value)}
                  placeholder="e.g. Westlake Warriors"
                  className="ai-setup-input"
                />
              </SettingRow>
              <SettingRow
                label="Level of play"
                hint="Used by AI to avoid over- or under-coaching concepts (Youth / MS / HS / College)."
              >
                <Select
                  value={form.levelOfPlay}
                  onChange={(v) => update("levelOfPlay", v)}
                  placeholder="Select level"
                  options={[
                    { value: "youth", label: "Youth" },
                    { value: "ms", label: "Middle School" },
                    { value: "hs", label: "High School" },
                    { value: "college", label: "College" },
                  ]}
                  className="ai-setup-input"
                />
              </SettingRow>
            </div>
            <SettingRow
              label="Offensive philosophy (high-level)"
              hint="Used by AI to contextualize offensive feedback and align with scheme intent."
            >
              <TextArea
                value={form.offensivePhilosophy}
                onChange={(e) => update("offensivePhilosophy", e.target.value)}
                rows={2}
                placeholder="e.g. Run-first, play-action, RPO-heavy"
                className="ai-setup-input"
              />
            </SettingRow>
            <SettingRow
              label="Defensive philosophy (high-level)"
              hint="Used by AI to reinforce assignment and intent on defense."
            >
              <TextArea
                value={form.defensivePhilosophy}
                onChange={(e) => update("defensivePhilosophy", e.target.value)}
                rows={2}
                placeholder="e.g. 4-3, cover 3, pressure on 3rd down"
                className="ai-setup-input"
              />
            </SettingRow>
            <SettingRow
              label="Special teams philosophy"
              hint="Used by AI to treat special teams as a priority phase and reinforce detail."
            >
              <TextArea
                value={form.specialTeamsPhilosophy}
                onChange={(e) => update("specialTeamsPhilosophy", e.target.value)}
                rows={2}
                className="ai-setup-input"
              />
            </SettingRow>
            <div className="ai-setup-fields-row">
              <SettingRow
                label="Experience level of players"
                hint="Used by AI to tailor complexity of explanations."
              >
                <Input
                  value={form.experienceLevel}
                  onChange={(e) => update("experienceLevel", e.target.value)}
                  placeholder="e.g. Mostly first-year varsity"
                  className="ai-setup-input"
                />
              </SettingRow>
              <SettingRow
                label="Season goals"
                hint="Used by AI to frame feedback (developmental, championship, rebuild, etc.)."
              >
                <Select
                  value={form.seasonGoals}
                  onChange={(v) => update("seasonGoals", v)}
                  placeholder="Select focus"
                  options={[
                    { value: "developmental", label: "Developmental" },
                    { value: "championship", label: "Championship" },
                    { value: "rebuild", label: "Rebuild" },
                  ]}
                  className="ai-setup-input"
                />
              </SettingRow>
            </div>
          </Card>
          <SaveFooter />
        </Panel>

        <Panel header="3. Team Culture" key="team-culture">
          <Card size="small" className="ai-setup-card">
            <div className="ai-setup-fields-row">
              <SettingRow
                label="Core values"
                hint="Used by AI to reinforce culture, not just technique (e.g. Effort, Toughness, Discipline, Trust)."
              >
                <TextArea
                  value={form.coreValues}
                  onChange={(e) => update("coreValues", e.target.value)}
                  rows={2}
                  placeholder="Comma-separated"
                  className="ai-setup-input"
                />
              </SettingRow>
              <SettingRow
                label="Non-negotiables"
                hint="Used by AI to align feedback with program identity."
              >
                <TextArea
                  value={form.nonNegotiables}
                  onChange={(e) => update("nonNegotiables", e.target.value)}
                  rows={2}
                  className="ai-setup-input"
                />
              </SettingRow>
            </div>
            <SettingRow
              label="How you define “doing it right”"
              hint="Used by AI to reinforce your standard of execution."
            >
              <TextArea
                value={form.doingItRight}
                onChange={(e) => update("doingItRight", e.target.value)}
                rows={2}
                className="ai-setup-input"
              />
            </SettingRow>
            <SettingRow
              label="Language you use often"
              hint="Used by AI to echo staff language (e.g. “Win your rep,” “Eyes right,” “Finish the play”)."
            >
              <TextArea
                value={form.languageOften}
                onChange={(e) => update("languageOften", e.target.value)}
                rows={2}
                placeholder="Phrases players hear in meetings"
                className="ai-setup-input"
              />
            </SettingRow>
            <div className="ai-setup-fields-row">
              <SettingRow
                label="What gets praised"
                hint="Used by AI to emphasize what you reward."
              >
                <Input
                  value={form.whatGetsPraised}
                  onChange={(e) => update("whatGetsPraised", e.target.value)}
                  className="ai-setup-input"
                />
              </SettingRow>
              <SettingRow
                label="What gets corrected immediately"
                hint="Used by AI to prioritize corrections that matter most to your staff."
              >
                <Input
                  value={form.whatGetsCorrected}
                  onChange={(e) => update("whatGetsCorrected", e.target.value)}
                  className="ai-setup-input"
                />
              </SettingRow>
            </div>
          </Card>
          <SaveFooter />
        </Panel>

        <Panel header="4. Frequently Asked Questions (Player-Facing)" key="faq">
          <Card size="small" className="ai-setup-card">
            <SettingRow
              label="Common player questions"
              hint="Used by AI to answer players instantly (e.g. “Why was I graded this way?” “What should I fix first?”)."
            >
              <TextArea
                value={form.faqQuestions}
                onChange={(e) => update("faqQuestions", e.target.value)}
                rows={3}
                placeholder="One per line or comma-separated"
                className="ai-setup-input"
              />
            </SettingRow>
            <SettingRow
              label="Preferred coach responses / teaching points"
              hint="Used by AI to reinforce consistent messaging across staff."
            >
              <TextArea
                value={form.preferredResponses}
                onChange={(e) => update("preferredResponses", e.target.value)}
                rows={3}
                className="ai-setup-input"
              />
            </SettingRow>
          </Card>
          <SaveFooter />
        </Panel>

        <Panel header="5. Offensive Coordinator" key="oc">
          <Card size="small" className="ai-setup-card">
            <SettingRow
              label="Offensive identity"
              hint="Used by AI to contextualize offensive feedback."
            >
              <TextArea
                value={form.ocIdentity}
                onChange={(e) => update("ocIdentity", e.target.value)}
                rows={2}
                className="ai-setup-input"
              />
            </SettingRow>
            <SettingRow
              label="Key concepts (run/pass family, situational priorities)"
              hint="Used by AI to align position coaching with scheme intent."
            >
              <TextArea
                value={form.ocConcepts}
                onChange={(e) => update("ocConcepts", e.target.value)}
                rows={3}
                placeholder="Down & distance, red zone, 3rd down emphasis"
                className="ai-setup-input"
              />
            </SettingRow>
          </Card>
          <SaveFooter />
        </Panel>

        <Panel header="6. Defensive Coordinator" key="dc">
          <Card size="small" className="ai-setup-card">
            <SettingRow
              label="Defensive identity & base fronts / coverages"
              hint="Used by AI to reinforce assignment and intent; help players connect technique to scheme."
            >
              <TextArea
                value={form.dcIdentity}
                onChange={(e) => update("dcIdentity", e.target.value)}
                rows={2}
                className="ai-setup-input"
              />
            </SettingRow>
            <SettingRow
              label="Run fit, coverage rules, tackling standards, DC language"
              hint="Used by AI to deliver defense-specific coaching in your staff’s language."
            >
              <TextArea
                value={form.dcConcepts}
                onChange={(e) => update("dcConcepts", e.target.value)}
                rows={3}
                className="ai-setup-input"
              />
            </SettingRow>
          </Card>
          <SaveFooter />
        </Panel>

        <Panel header="7. Special Teams Coordinator" key="st">
          <Card size="small" className="ai-setup-card">
            <SettingRow
              label="Special teams philosophy, effort & finish standards"
              hint="Used by AI to treat special teams as a priority and reinforce detail and effort."
            >
              <TextArea
                value={form.stPhilosophy}
                onChange={(e) => update("stPhilosophy", e.target.value)}
                rows={3}
                placeholder="Technique priorities, common mistakes, terminology"
                className="ai-setup-input"
              />
            </SettingRow>
          </Card>
        </Panel>

        <Panel header="8. Position Group Intelligence" key="position">
          <Card size="small" className="ai-setup-card">
            <p className="ai-setup-block-hint">
              Scale position coaching beyond meeting time. Define per position: responsibilities, technique standards,
              coaching cues, drills, common errors, film teaching points, what you grade hardest, and what separates
              average vs great. Used by AI to deliver position-specific coaching and reinforce exact language players
              hear in meetings.
            </p>
            <Collapse className="ai-setup-position-collapse" defaultActiveKey={[]}>
              {POSITIONS.map((pos) => (
                <Panel header={pos} key={pos}>
                  <SettingRow
                    label="Position responsibilities"
                    hint="Used by AI to frame feedback around this position’s role in your scheme."
                  >
                    <TextArea
                      value={positionIntel[pos].responsibilities}
                      onChange={(e) => updatePosition(pos, "responsibilities", e.target.value)}
                      rows={2}
                      placeholder="Key jobs and assignments"
                      className="ai-setup-input"
                    />
                  </SettingRow>
                  <SettingRow
                    label="Technique standards"
                    hint="Used by AI to reinforce your technique expectations for this position."
                  >
                    <TextArea
                      value={positionIntel[pos].techniqueStandards}
                      onChange={(e) => updatePosition(pos, "techniqueStandards", e.target.value)}
                      rows={2}
                      className="ai-setup-input"
                    />
                  </SettingRow>
                  <SettingRow
                    label="Coaching cues (short, repeatable)"
                    hint="Used by AI to echo the exact language players hear in meetings."
                  >
                    <TextArea
                      value={positionIntel[pos].coachingCues}
                      onChange={(e) => updatePosition(pos, "coachingCues", e.target.value)}
                      rows={2}
                      placeholder="e.g. Eyes right, win your rep"
                      className="ai-setup-input"
                    />
                  </SettingRow>
                  <SettingRow
                    label="Drills you use (and what they teach)"
                    hint="Used by AI to connect feedback to specific drills and learning goals."
                  >
                    <TextArea
                      value={positionIntel[pos].drills}
                      onChange={(e) => updatePosition(pos, "drills", e.target.value)}
                      rows={2}
                      className="ai-setup-input"
                    />
                  </SettingRow>
                  <SettingRow
                    label="Common errors & corrections"
                    hint="Used by AI to give consistent corrections and next-rep solutions."
                  >
                    <TextArea
                      value={positionIntel[pos].commonErrors}
                      onChange={(e) => updatePosition(pos, "commonErrors", e.target.value)}
                      rows={2}
                      className="ai-setup-input"
                    />
                  </SettingRow>
                  <SettingRow
                    label="Film teaching points"
                    hint="Used by AI to reference film language and teaching points you use."
                  >
                    <TextArea
                      value={positionIntel[pos].filmPoints}
                      onChange={(e) => updatePosition(pos, "filmPoints", e.target.value)}
                      rows={2}
                      className="ai-setup-input"
                    />
                  </SettingRow>
                  <SettingRow
                    label="What you grade the hardest"
                    hint="Used by AI to prioritize feedback on what matters most to you."
                  >
                    <Input
                      value={positionIntel[pos].gradeHardest}
                      onChange={(e) => updatePosition(pos, "gradeHardest", e.target.value)}
                      className="ai-setup-input"
                    />
                  </SettingRow>
                  <SettingRow
                    label="What separates average vs great at this position"
                    hint="Used by AI to set the standard and push players toward excellence."
                  >
                    <TextArea
                      value={positionIntel[pos].averageVsGreat}
                      onChange={(e) => updatePosition(pos, "averageVsGreat", e.target.value)}
                      rows={2}
                      className="ai-setup-input"
                    />
                  </SettingRow>
                </Panel>
              ))}
            </Collapse>
          </Card>
          <SaveFooter />
        </Panel>

        <Panel header="9. Analytic Insights" key="analytics">
          <Card size="small" className="ai-setup-card">
            <SettingRow
              label="Key metrics that matter most"
              hint="Used by AI to explain analytics in player-friendly language and turn numbers into actionable improvement."
            >
              <TextArea
                value={form.keyMetrics}
                onChange={(e) => update("keyMetrics", e.target.value)}
                rows={2}
                placeholder="e.g. Effort grade, execution by down, consistency"
                className="ai-setup-input"
              />
            </SettingRow>
            <SettingRow
              label="How players should use their data / effort vs execution"
              hint="Used by AI to keep analytics tied to improvement, not ego; prevent chasing stats."
            >
              <TextArea
                value={form.effortVsExecution}
                onChange={(e) => update("effortVsExecution", e.target.value)}
                rows={3}
                className="ai-setup-input"
              />
            </SettingRow>
          </Card>
          <SaveFooter />
        </Panel>

        <Panel header="10. How the AI Responds" key="agent-behavior">
          <Card size="small" className="ai-setup-card">
            <SettingRow
              label="How consistent the AI sounds"
              hint="Used by the AI to keep feedback repeatable. More consistent = same kind of language every time; more varied = different phrasing. We recommend consistent for coaching."
            >
              <Select
                value={String(form.temperature)}
                onChange={(v) => update("temperature", Number(v))}
                options={[
                  { value: "0.1", label: "Very consistent" },
                  { value: "0.3", label: "Consistent (recommended)" },
                  { value: "0.5", label: "Balanced" },
                  { value: "0.7", label: "More varied" },
                ]}
                className="ai-setup-input"
              />
            </SettingRow>
            <SettingRow
              label="How long answers can be"
              hint="Used by the AI to keep replies short (quick cues) or allow longer explanations when players need more detail."
            >
              <Select
                value={String(form.maxResponseTokens)}
                onChange={(v) => update("maxResponseTokens", Number(v))}
                options={[
                  { value: "150", label: "1–2 sentences" },
                  { value: "300", label: "Short coaching cue" },
                  { value: "500", label: "Medium" },
                  { value: "800", label: "Detailed" },
                ]}
                className="ai-setup-input"
              />
            </SettingRow>
            <SettingRow
              label="Remember what was just said in the chat"
              hint="When on, the AI can use the last few messages so its answers fit the conversation. When off, each answer is more standalone."
            >
              <Switch
                checked={form.useConversationMemory}
                onChange={(v) => update("useConversationMemory", v)}
              />
            </SettingRow>
            <SettingRow
              label="When to suggest “talk to your coach”"
              hint="After this many back-and-forths, the AI will suggest the player bring the conversation to you so it doesn’t replace real dialogue."
            >
              <Select
                value={String(form.maxConversationTurns)}
                onChange={(v) => update("maxConversationTurns", Number(v))}
                options={[
                  { value: "5", label: "5 exchanges" },
                  { value: "10", label: "10 exchanges" },
                  { value: "15", label: "15 exchanges" },
                  { value: "20", label: "20 exchanges" },
                ]}
                className="ai-setup-input"
              />
            </SettingRow>
          </Card>
          <SaveFooter />
        </Panel>

        <Panel header="11. What the AI Can Look Up" key="mcp-tools">
          <Card size="small" className="ai-setup-card">
            <p className="ai-setup-block-hint">
              Choose what information the AI can use when answering players. Turn off anything you don’t want it to reference.
            </p>
            <SettingRow
              label="Player stats"
              hint="Used by the AI to reference current stats and trends when discussing performance."
            >
              <Switch
                checked={form.enabledMcpTools?.includes("player-stats")}
                onChange={(v) =>
                  update(
                    "enabledMcpTools",
                    v
                      ? [...(form.enabledMcpTools || []), "player-stats"]
                      : (form.enabledMcpTools || []).filter((t) => t !== "player-stats")
                  )
                }
              />
            </SettingRow>
            <SettingRow
              label="Playbook / scheme"
              hint="Used by the AI to reference plays, formations, and scheme when answering X’s and O’s questions."
            >
              <Switch
                checked={form.enabledMcpTools?.includes("playbook")}
                onChange={(v) =>
                  update(
                    "enabledMcpTools",
                    v
                      ? [...(form.enabledMcpTools || []), "playbook"]
                      : (form.enabledMcpTools || []).filter((t) => t !== "playbook")
                  )
                }
              />
            </SettingRow>
            <SettingRow
              label="Past feedback you’ve given"
              hint="Used by the AI to reference your previous feedback so its answers stay consistent with what you’ve already said."
            >
              <Switch
                checked={form.enabledMcpTools?.includes("feedback-history")}
                onChange={(v) =>
                  update(
                    "enabledMcpTools",
                    v
                      ? [...(form.enabledMcpTools || []), "feedback-history"]
                      : (form.enabledMcpTools || []).filter((t) => t !== "feedback-history")
                  )
                }
              />
            </SettingRow>
            <SettingRow
              label="Schedule / calendar"
              hint="Used by the AI to answer when practices, meetings, or games are."
            >
              <Switch
                checked={form.enabledMcpTools?.includes("schedule")}
                onChange={(v) =>
                  update(
                    "enabledMcpTools",
                    v
                      ? [...(form.enabledMcpTools || []), "schedule"]
                      : (form.enabledMcpTools || []).filter((t) => t !== "schedule")
                  )
                }
              />
            </SettingRow>
          </Card>
          <SaveFooter />
        </Panel>

        <Panel header="12. When to Involve You" key="guardrails">
          <Card size="small" className="ai-setup-card">
            <SettingRow
              label="Topics the AI should not answer"
              hint="The AI will decline and suggest the player talk to you about these. Keeps sensitive decisions with you and your staff."
            >
              <TextArea
                value={form.offLimitsTopics}
                onChange={(e) => update("offLimitsTopics", e.target.value)}
                rows={3}
                placeholder="e.g. playing time, recruiting, medical, other players"
                className="ai-setup-input"
              />
            </SettingRow>
            <SettingRow
              label="When the AI should point players to you"
              hint="In these situations the AI will suggest “talk to your coach”—e.g. sensitive topics, playing time, health, or when the player asks for you."
            >
              <TextArea
                value={form.escalationWhen}
                onChange={(e) => update("escalationWhen", e.target.value)}
                rows={2}
                className="ai-setup-input"
              />
            </SettingRow>
          </Card>
          <SaveFooter />
        </Panel>
      </Collapse>
      </div>
    </ConfigProvider>
  );
};

const mapStateToProps = (state) => ({
  coachProfile: _.get(state, "app.account.profile.data.details"),
});

const mapDispatchToProps = (dispatch) => ({
  getAiCoachConfig: (opts) => dispatch(global.redux.action.schools.getAiCoachConfig(opts)),
  saveAiCoachConfig: (opts) => dispatch(global.redux.action.schools.saveAiCoachConfig(opts)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AICoachSetup);
